import { SITE_INTEGRATION_TYPE } from "../constant/site";

export type SiteIntegrationInfo =
  | ICafe24IntegrationInfo
  | ISixshopIntegrationInfo
  | IShopifyIntegrationInfo
  | INhnIntegrationInfo
  | IMakeshopIntegrationInfo
  | IGodomallIntegrationInfo
  | IWordpressIntegrationInfo
  | ISixshopProIntegrationInfo;

interface IBaseIntegrationInfo {
  type: SITE_INTEGRATION_TYPE;
}

interface ICafe24IntegrationInfo extends IBaseIntegrationInfo {
  type: SITE_INTEGRATION_TYPE.CAFE24;
  mallId: string;
  shopNo?: string;
}

interface ISixshopIntegrationInfo extends IBaseIntegrationInfo {
  type: SITE_INTEGRATION_TYPE.SIXSHOP;
  // TODO: 수정
  oo: string;
}

interface IShopifyIntegrationInfo extends IBaseIntegrationInfo {
  type: SITE_INTEGRATION_TYPE.SHOPIFY;
  // TODO: 수정
  oo: string;
}

interface INhnIntegrationInfo extends IBaseIntegrationInfo {
  type: SITE_INTEGRATION_TYPE.NHN;
  mallNo: number;
}

interface IMakeshopIntegrationInfo extends IBaseIntegrationInfo {
  type: SITE_INTEGRATION_TYPE.MAKESHOP;
  mallId: string;
}

interface IGodomallIntegrationInfo extends IBaseIntegrationInfo {
  type: SITE_INTEGRATION_TYPE.GODOMALL;
  mallId: string;
}

interface IWordpressIntegrationInfo extends IBaseIntegrationInfo {
  type: SITE_INTEGRATION_TYPE.WORDPRESS;
  mallId: string;
}

interface ISixshopProIntegrationInfo extends IBaseIntegrationInfo {
  type: SITE_INTEGRATION_TYPE.SIXSHOP_PRO;
  storeId: string;
}

export const getCafe24ClientInfoFromToken = (token: {
  publicId: string;
  accessToken: string;
  refreshToken: string;
}) => {
  const { accessToken, publicId, refreshToken } = token;
  const [_integrationString, mallId] = publicId.split(":");

  if (!mallId) {
    throw new Error("Invalid publicId: " + publicId);
  }

  return {
    accessToken,
    mallId,
    refreshToken,
  };
};

export const getNhnMallNoFromToken = (token: { publicId: string }): number => {
  const { publicId } = token;
  const [_integrationString, mallNo] = publicId.split(":");

  if (!mallNo) {
    throw new Error("Invalid publicId: " + publicId);
  }

  return Number(mallNo);
};

// TODO: 추후 mallId 구분 필요 시 해당 함수 사용
export const getMakeshopMallIdFromToken = (token: {
  publicId: string;
}): string => {
  const { publicId } = token;
  const [_integrationString, mallNo] = publicId.split(":");

  if (!mallNo) {
    throw new Error("Invalid publicId: " + publicId);
  }

  return mallNo;
};

// TODO: 추후 mallId 구분 필요 시 해당 함수 사용
export const getGodomallMallIdFromToken = (token: {
  publicId: string;
}): string => {
  const { publicId } = token;
  const [_integrationString, mallId] = publicId.split(":");

  if (!mallId) {
    throw new Error("Invalid publicId: " + publicId);
  }

  return mallId;
};

export const getWordpressMallIdFromToken = (token: {
  publicId: string;
}): string => {
  const { publicId } = token;
  const [_integrationString, mallId] = publicId.split(":");

  if (!mallId) {
    throw new Error("Invalid publicId: " + publicId);
  }

  return mallId;
};

export const getSixshopProClientInfoFromToken = (token: {
  publicId: string;
  accessToken: string;
  refreshToken: string;
}) => {
  const { accessToken, publicId, refreshToken } = token;
  const [_integrationString, storeId] = publicId.split(":");

  if (!storeId) {
    throw new Error("Invalid publicId: " + publicId);
  }

  return {
    accessToken,
    storeId,
    refreshToken,
  };
};

export const createCafe24Sk = (mallId: string, shopNo?: string) => {
  return `cafe24:${mallId}_${shopNo ?? ""}`;
};

export const createNhnSk = (mallNo: number) => {
  return `nhn:${mallNo}`;
};

export const createWordpressSk = (mallId: string) => {
  return `wordpress:${mallId}`;
};

// TODO: 추후 mallId 구분 필요 시 해당 함수 사용
export const createMakeshopSk = (mallId: string) => {
  return `make:${mallId}`;
};

// TODO: 추후 mallId 구분 필요 시 해당 함수 사용
export const createGodomallSk = (mallId: string) => {
  return `godomall:${mallId}`;
};

export const createSixshopProSk = (storeId: string) => {
  return `sixshop_pro:${storeId}`;
};

export const getTypedIntegration = <T extends { sk: string }>(
  integration: T
) => {
  const result: T & SiteIntegrationInfo = {
    ...integration,
    ...getIntegrationBySk(integration.sk),
  };
  return result;
};

export const getIntegrationBySk = (publicId: string): SiteIntegrationInfo => {
  const [integrationString, value] = publicId.split(":");

  if (!integrationString || !value) {
    throw new Error("Invalid publicId");
  }

  const integrationTypeEnumValue =
    integrationTypeStringToEnum(integrationString);

  switch (integrationTypeEnumValue) {
    case SITE_INTEGRATION_TYPE.CAFE24: {
      const splitted = value.split("_");
      const mallId = splitted[0] ?? "";
      const shopNo = splitted[1];
      return {
        type: SITE_INTEGRATION_TYPE.CAFE24,
        mallId,
        shopNo,
      };
    }
    case SITE_INTEGRATION_TYPE.NHN: {
      return {
        type: SITE_INTEGRATION_TYPE.NHN,
        mallNo: Number(value),
      };
    }

    case SITE_INTEGRATION_TYPE.MAKESHOP: {
      return {
        type: SITE_INTEGRATION_TYPE.MAKESHOP,
        mallId: value,
      };
    }

    case SITE_INTEGRATION_TYPE.GODOMALL: {
      return {
        type: SITE_INTEGRATION_TYPE.GODOMALL,
        mallId: value,
      };
    }

    case SITE_INTEGRATION_TYPE.WORDPRESS: {
      return {
        type: SITE_INTEGRATION_TYPE.WORDPRESS,
        mallId: value,
      };
    }

    case SITE_INTEGRATION_TYPE.SIXSHOP_PRO: {
      return {
        type: SITE_INTEGRATION_TYPE.SIXSHOP_PRO,
        storeId: value,
      };
    }

    case SITE_INTEGRATION_TYPE.SHOPIFY:
    case SITE_INTEGRATION_TYPE.SIXSHOP:
      throw new Error("Not implemented");
  }
};

export const createTokenPublicId = (info: SiteIntegrationInfo) => {
  switch (info.type) {
    case SITE_INTEGRATION_TYPE.CAFE24:
      return "cafe24:" + info.mallId;

    case SITE_INTEGRATION_TYPE.NHN:
      return "nhn:" + info.mallNo;

    case SITE_INTEGRATION_TYPE.MAKESHOP:
      return `makeshop:${info.mallId}`;

    case SITE_INTEGRATION_TYPE.WORDPRESS:
      return `wordpress:${info.mallId}`;

    case SITE_INTEGRATION_TYPE.GODOMALL:
      return `godomall:${info.mallId}`;

    case SITE_INTEGRATION_TYPE.SIXSHOP_PRO:
      return `sixshop_pro:${info.storeId}`;

    case SITE_INTEGRATION_TYPE.SIXSHOP:
    case SITE_INTEGRATION_TYPE.SHOPIFY:
      throw new Error("Not implemented");
  }
};

// prefix가 제거된 siteIntegration의 publicId에서 shop_no와 mallId를 반환한다.
export const integrationTypeStringToEnum = (type: string) => {
  switch (type) {
    case "cafe24":
      return SITE_INTEGRATION_TYPE.CAFE24;
    case "shopify":
      return SITE_INTEGRATION_TYPE.SHOPIFY;
    case "sixshop":
      return SITE_INTEGRATION_TYPE.SIXSHOP;
    case "nhn":
      return SITE_INTEGRATION_TYPE.NHN;
    case "makeshop":
      return SITE_INTEGRATION_TYPE.MAKESHOP;
    case "godomall":
      return SITE_INTEGRATION_TYPE.GODOMALL;
    case "wordpress":
      return SITE_INTEGRATION_TYPE.WORDPRESS;
    case "sixshop_pro":
      return SITE_INTEGRATION_TYPE.SIXSHOP_PRO;
  }
  throw new Error("Invalid integration type");
};
